import React, { useState, useEffect, useCallback } from 'react';
import { verifyConnectionDetailed } from '@/core/api/supabase';
import { supabase } from '@/core/api/supabase';
import { useConnectionStore } from '@/lib/stores/connection-store';
import { AuthChangeEvent, Session } from '@supabase/supabase-js';

interface ConnectionStatusProps {
  /** When true, show detailed connection information (for admin/debug views) */
  showDetails?: boolean;
  /** When true, include a retry button */
  showRetry?: boolean;
  /** Custom CSS class for the container */
  className?: string;
}

/**
 * Connection status indicator that shows real-time Supabase connection state
 * Designed to be shown in header or as an overlay when connection is lost
 */
export const ConnectionStatus: React.FC<ConnectionStatusProps> = ({
  showDetails = false,
  showRetry = true,
  className = '',
}) => {
  const { lastCheck } = useConnectionStore();
  const [checking, setChecking] = useState(false);
  const [details, setDetails] = useState<{
    networkConnected: boolean;
    tokenValid: boolean;
    dataAccessible: boolean;
    message: string;
  } | null>(null);

  // Format relative time since last check
  const getTimeAgo = () => {
    const seconds = Math.floor((Date.now() - lastCheck) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
  };

  // Manually check connection and update status
  const checkConnection = useCallback(async () => {
    if (checking) return;
    
    setChecking(true);
    try {
      // Check network connection
      const result = await verifyConnectionDetailed();
      setDetails(result);
      
      // If network is connected, force the status to show connected
      // This ignores 401 errors from the health endpoint
      if (result.networkConnected) {
        setForceConnected(true);
      } else {
        setForceConnected(false);
      }
      
      // Also check authentication status
      const { data } = await supabase.auth.getSession();
      setIsAuthenticated(!!data.session);
    } finally {
      setChecking(false);
    }
  }, [checking, setChecking, setDetails]);

  // Initial detailed check
  useEffect(() => {
    if (showDetails && !details) {
      checkConnection();
    }
  }, [showDetails, details, checkConnection]);

  // Track both network connectivity and authentication status
  const [forceConnected, setForceConnected] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Check authentication status
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        setIsAuthenticated(!!data.session);
      } catch (e) {

        setIsAuthenticated(false);
      }
    };
    
    checkAuthStatus();
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => {
        setIsAuthenticated(!!session);
      }
    );
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);
  
  // Status indicators based on both network and auth status
  const getStatusInfo = () => {
    if (!forceConnected) {
      return { color: 'bg-red-500', text: 'Disconnected' };
    }
    
    if (!isAuthenticated) {
      return { color: 'bg-yellow-500', text: 'Connected (Not Authenticated)' };
    }
    
    return { color: 'bg-green-500', text: 'Connected' };
  };
  
  const { color: statusColor, text: statusText } = getStatusInfo();
  
  // Force check connection on initial render
  useEffect(() => {
    // Initial connection check with a slight delay to allow initialization
    const timer = setTimeout(() => {
      checkConnection();
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [checkConnection]);

  return (
    <div className={`flex items-center ${className}`}>
      <div className="flex items-center gap-2" data-component-name="ConnectionStatus">
        <span className={`h-3 w-3 rounded-full ${statusColor}`} aria-hidden="true" />
        <span className="text-sm font-medium">{statusText}</span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {getTimeAgo()}
        </span>
        
        {showRetry && (
          <button
            onClick={checkConnection}
            disabled={checking}
            className="ml-2 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            aria-label="Verify connection"
          >
            {checking ? 'Checking...' : 'Verify'}
          </button>
        )}
      </div>

      {showDetails && details && (
        <div className="mt-2 text-xs">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="font-medium">Network:</span>{' '}
              <span className={details.networkConnected ? 'text-green-600' : 'text-red-600'}>
                {details.networkConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
            <div>
              <span className="font-medium">Auth Token:</span>{' '}
              <span className={details.tokenValid ? 'text-green-600' : 'text-red-600'}>
                {details.tokenValid ? 'Valid' : 'Invalid'}
              </span>
            </div>
            <div>
              <span className="font-medium">Data Access:</span>{' '}
              <span className={details.dataAccessible ? 'text-green-600' : 'text-red-600'}>
                {details.dataAccessible ? 'Available' : 'Unavailable'}
              </span>
            </div>
          </div>
          {details.message && (
            <div className="mt-1 text-gray-700 dark:text-gray-300">{details.message}</div>
          )}
        </div>
      )}
    </div>
  );
};
